import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Image,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  VStack,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { FaExternalLinkAlt, FaTimes, FaYoutube } from 'react-icons/fa';
import Helmet from 'react-helmet';

// I dati ora vengono caricati dinamicamente da Strapi

// Helper function per estrarre l'URL della thumbnail da Strapi v5/v4
const getThumbnailUrl = (thumbnail) => {
  if (!thumbnail) return null;

  let url = null;

  // Strapi v5
  if (thumbnail.url) {
    url = thumbnail.url;
  }
  // Strapi v4 fallback
  else if (
    thumbnail.data &&
    thumbnail.data.attributes &&
    thumbnail.data.attributes.url
  ) {
    url = thumbnail.data.attributes.url;
  }

  if (!url) return null;

  // Se l'URL è già assoluto, usalo direttamente
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Altrimenti costruisci l'URL usando Strapi API URL
  return `${process.env.REACT_APP_STRAPI_API_URL || ''}${url}`;
};

// Helper function per estrarre gli URL della gallery da Strapi v5/v4
const getGalleryUrls = (gallery) => {
  if (!gallery || !Array.isArray(gallery)) return [];

  return gallery
    .map((img) => {
      // Strapi v5
      if (img.url) {
        return `${process.env.REACT_APP_STRAPI_API_URL || ''}${img.url}`;
      }

      // Strapi v4 fallback
      if (img.data && img.data.attributes && img.data.attributes.url) {
        return `${process.env.REACT_APP_STRAPI_API_URL || ''}${img.data.attributes.url}`;
      }

      return null;
    })
    .filter((url) => url !== null);
};

const ArchivioAttivita = () => {
  const [progettiArchiviati, setProgettiArchiviati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAttivita, setSelectedAttivita] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Funzione per caricare i progetti archiviati da Strapi
  const fetchProgettiArchiviati = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/projects-archived');
      const result = await response.json();

      if (result.success) {
        // Filtra anche lato client per clientSide === true (backup, anche se l'endpoint dovrebbe già farlo)
        const allProjects = result.data || [];
        const clientSideProjects = allProjects.filter((project) => {
          // Gestisce sia la struttura normalizzata che quella con attributes (Strapi v5)
          const clientSide =
            project.clientSide ?? project.attributes?.clientSide;
          return clientSide === true;
        });
        setProgettiArchiviati(clientSideProjects);
      } else {
        setError(
          result.error || 'Errore nel caricamento dei progetti archiviati'
        );
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Errore nel fetch dei progetti archiviati:', err);
    } finally {
      setLoading(false);
    }
  };

  // Carica i progetti archiviati al mount del componente
  useEffect(() => {
    fetchProgettiArchiviati();
  }, []);

  const handleCardClick = (attivita) => {
    setSelectedAttivita(attivita);
    setCurrentImageIndex(0); // Reset dell'indice immagine quando si apre un nuovo progetto
    onOpen();
  };

  // Funzioni per navigare la gallery
  const nextImage = () => {
    if (selectedAttivita && selectedAttivita.gallery) {
      const galleryUrls = getGalleryUrls(selectedAttivita.gallery);
      if (galleryUrls.length > 0) {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === galleryUrls.length - 1 ? 0 : prevIndex + 1
        );
      }
    }
  };

  const prevImage = () => {
    if (selectedAttivita && selectedAttivita.gallery) {
      const galleryUrls = getGalleryUrls(selectedAttivita.gallery);
      if (galleryUrls.length > 0) {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === 0 ? galleryUrls.length - 1 : prevIndex - 1
        );
      }
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const closeModal = () => {
    onClose();
    setSelectedAttivita(null);
    setCurrentImageIndex(0);
  };

  // Helper per ottenere l'URL dell'immagine per i meta tags
  const getImageUrl = () => {
    if (selectedAttivita) {
      const thumbnailUrl = getThumbnailUrl(selectedAttivita.thumbnail);
      if (thumbnailUrl) {
        return thumbnailUrl;
      }
    }
    return `${window.location.origin}/logo.png`;
  };

  // Helper per ottenere il titolo per i meta tags
  const getPageTitle = () => {
    if (selectedAttivita?.titoloProgetto || selectedAttivita?.titolo) {
      return `${selectedAttivita.titoloProgetto || selectedAttivita.titolo} - CIL - Creatività in Libertà`;
    }
    return 'Archivio Attività - CIL - Creatività in Libertà';
  };

  // Helper per ottenere la descrizione per i meta tags
  const getPageDescription = () => {
    if (selectedAttivita?.descrizione) {
      return selectedAttivita.descrizione.substring(0, 200);
    }
    return 'Scopri tutti i progetti completati di CIL – Creatività in Libertà. Testimonianze concrete del nostro impegno per la comunità attraverso creatività, gioco, partecipazione e cura del territorio.';
  };

  return (
    <Box bg="#36393f" minH="100vh" py={12} color="white">
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta
          property="og:title"
          content={
            selectedAttivita?.titoloProgetto ||
            selectedAttivita?.titolo ||
            'Archivio Attività'
          }
        />
        <meta property="og:description" content={getPageDescription()} />
        <meta property="og:image" content={getImageUrl()} />
        <meta
          property="og:url"
          content={`${window.location.origin}/archivio-2025`}
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            selectedAttivita?.titoloProgetto ||
            selectedAttivita?.titolo ||
            'Archivio Attività'
          }
        />
        <meta name="twitter:description" content={getPageDescription()} />
        <meta name="twitter:image" content={getImageUrl()} />
      </Helmet>
      <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
        {/* Header con titolo */}
        <VStack spacing={6} mb={16} textAlign="center">
          <Heading
            as="h1"
            fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
            fontWeight="extrabold"
            color="white"
            mb={4}
            fontFamily="Poppins, sans-serif"
          >
            Archivio Attività
          </Heading>

          <Text
            fontSize={{ base: 'lg', md: 'xl' }}
            color="gray.300"
            maxW="3xl"
            lineHeight="1.6"
            fontFamily="Montserrat, sans-serif"
          >
            Scopri tutti i progetti completati di CIL – Creatività in Libertà.
            In questa sezione trovi tutte le attività che sono state portate a
            termine, testimonianze concrete del nostro impegno per la comunità
            attraverso creatività, gioco, partecipazione e cura del territorio.
          </Text>
        </VStack>

        {/* Grid delle attività */}
        {loading ? (
          <Box textAlign="center" py={12}>
            <Text fontSize="lg" color="gray.300">
              Caricamento progetti archiviati...
            </Text>
          </Box>
        ) : error ? (
          <Box textAlign="center" py={12}>
            <Text fontSize="lg" color="red.400">
              Errore: {error}
            </Text>
          </Box>
        ) : progettiArchiviati.length === 0 ? (
          <Box textAlign="center" py={12}>
            <Text fontSize="lg" color="gray.300">
              Nessun progetto archiviato trovato.
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} mb={12}>
            {progettiArchiviati.map((attivita) => (
              <Box
                key={attivita.id}
                bg="#36393f"
                borderRadius="20px"
                overflow="hidden"
                boxShadow="xl"
                border="1px solid"
                borderColor="gray.600"
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  borderColor: '#ffcc00',
                }}
                cursor="pointer"
                onClick={() => handleCardClick(attivita)}
              >
                {/* Thumbnail */}
                <Box
                  position="relative"
                  overflow="hidden"
                  sx={{ aspectRatio: '16 / 10' }}
                >
                  <Image
                    position="absolute"
                    inset={0}
                    src={
                      getThumbnailUrl(attivita.thumbnail) || attivita.immagine
                    }
                    alt={attivita.titoloProgetto || attivita.titolo}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    transition="transform 0.3s ease"
                    _hover={{ transform: 'scale(1.05)' }}
                  />
                </Box>

                {/* Contenuto */}
                <Box p={6}>
                  {/* Header con titolo */}
                  <VStack spacing={4} align="stretch" mb={4}>
                    <Heading
                      as="h3"
                      fontSize="xl"
                      fontWeight="bold"
                      color="white"
                      fontFamily="Poppins, sans-serif"
                      lineHeight="1.2"
                    >
                      {attivita.titoloProgetto || attivita.titolo}
                    </Heading>
                  </VStack>

                  {/* Descrizione */}
                  <Text
                    fontSize="md"
                    color="gray.300"
                    lineHeight="1.6"
                    fontFamily="Montserrat, sans-serif"
                    noOfLines={5}
                  >
                    {attivita.descrizione}
                  </Text>

                  {/* Pulsante Scopri di più */}
                  <Button
                    onClick={() => handleCardClick(attivita)}
                    bg="#ffcc00"
                    color="black"
                    borderRadius="12px"
                    fontWeight="600"
                    _hover={{
                      bg: '#e6b800',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
                    }}
                    transition="all 0.3s ease"
                    size="md"
                    w="full"
                    fontFamily="Montserrat, sans-serif"
                    mt={6}
                  >
                    Scopri di più
                  </Button>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        )}

        {/* Modal per i dettagli */}
        <Modal
          isOpen={isOpen}
          onClose={closeModal}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent
            mx={4}
            maxH="90vh"
            mb={8}
            bg="#2f3136"
            borderRadius="20px"
            border="1px solid"
            borderColor="gray.600"
          >
            <ModalHeader bg="#ffcc00" color="black" borderTopRadius="20px">
              <HStack justify="space-between" align="center">
                <Heading size="lg">
                  {selectedAttivita?.titoloProgetto || selectedAttivita?.titolo}
                </Heading>
                <Button
                  onClick={closeModal}
                  variant="ghost"
                  color="black"
                  _hover={{ bg: 'rgba(0,0,0,0.1)' }}
                  p={2}
                >
                  <Icon as={FaTimes} />
                </Button>
              </HStack>
            </ModalHeader>

            <ModalBody p={0}>
              {selectedAttivita && (
                <VStack spacing={0} align="stretch">
                  {/* Immagine principale */}
                  <Box position="relative" h="300px">
                    <Image
                      src={
                        getThumbnailUrl(selectedAttivita.thumbnail) ||
                        selectedAttivita.immagine
                      }
                      alt={
                        selectedAttivita.titoloProgetto ||
                        selectedAttivita.titolo
                      }
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                  </Box>

                  {/* Gallery */}
                  {(() => {
                    const galleryUrls = getGalleryUrls(
                      selectedAttivita.gallery
                    );
                    return galleryUrls.length > 0 ? (
                      <Box p={6} bg="#36393f">
                        {/* Immagine principale con navigazione */}
                        <Box position="relative" mb={4}>
                          <Image
                            src={galleryUrls[currentImageIndex]}
                            alt={`${selectedAttivita.titoloProgetto || selectedAttivita.titolo} - Immagine ${currentImageIndex + 1}`}
                            borderRadius="lg"
                            objectFit="cover"
                            height="400px"
                            width="100%"
                          />

                          {/* Frecce di navigazione */}
                          {galleryUrls.length > 1 && (
                            <>
                              <Button
                                position="absolute"
                                left={2}
                                top="50%"
                                transform="translateY(-50%)"
                                onClick={prevImage}
                                bg="rgba(0,0,0,0.7)"
                                color="white"
                                _hover={{ bg: 'rgba(0,0,0,0.9)' }}
                                borderRadius="full"
                                size="sm"
                                minW="40px"
                                h="40px"
                              >
                                ‹
                              </Button>
                              <Button
                                position="absolute"
                                right={2}
                                top="50%"
                                transform="translateY(-50%)"
                                onClick={nextImage}
                                bg="rgba(0,0,0,0.7)"
                                color="white"
                                _hover={{ bg: 'rgba(0,0,0,0.9)' }}
                                borderRadius="full"
                                size="sm"
                                minW="40px"
                                h="40px"
                              >
                                ›
                              </Button>
                            </>
                          )}

                          {/* Indicatore posizione */}
                          {galleryUrls.length > 1 && (
                            <Text
                              position="absolute"
                              bottom={2}
                              right={2}
                              bg="rgba(0,0,0,0.7)"
                              color="white"
                              px={2}
                              py={1}
                              borderRadius="md"
                              fontSize="sm"
                              fontFamily="Montserrat, sans-serif"
                            >
                              {currentImageIndex + 1} / {galleryUrls.length}
                            </Text>
                          )}
                        </Box>

                        {/* Thumbnails per navigazione rapida */}
                        {galleryUrls.length > 1 && (
                          <SimpleGrid
                            columns={{ base: 3, md: 4, lg: 6 }}
                            spacing={2}
                          >
                            {galleryUrls.map((img, index) => (
                              <Image
                                key={`gallery-${selectedAttivita.id}-${img.split('/').pop().split('?')[0]}`}
                                src={img}
                                alt={`Thumbnail ${index + 1}`}
                                borderRadius="md"
                                objectFit="cover"
                                height="80px"
                                width="100%"
                                cursor="pointer"
                                border={
                                  currentImageIndex === index
                                    ? '3px solid #ffcc00'
                                    : '1px solid #e2e8f0'
                                }
                                _hover={{
                                  transform: 'scale(1.05)',
                                  transition: 'all 0.2s',
                                  borderColor: '#ffcc00',
                                }}
                                onClick={() => goToImage(index)}
                              />
                            ))}
                          </SimpleGrid>
                        )}
                      </Box>
                    ) : null;
                  })()}

                  {/* Contenuto principale */}
                  <Box p={6}>
                    {/* Descrizione */}
                    <Box mb={6}>
                      <Text
                        color="gray.300"
                        lineHeight="tall"
                        fontFamily="Montserrat, sans-serif"
                      >
                        {selectedAttivita.descrizione}
                      </Text>
                    </Box>

                    {/* Descrizione lunga se disponibile */}
                    {selectedAttivita.longDescription && (
                      <Box mb={6}>
                        <Text
                          color="gray.300"
                          lineHeight="tall"
                          fontFamily="Montserrat, sans-serif"
                        >
                          {selectedAttivita.longDescription}
                        </Text>
                      </Box>
                    )}

                    {/* Link e azioni */}
                    <VStack spacing={4} align="stretch">
                      {selectedAttivita.urlLink && (
                        <Button
                          as="a"
                          href={selectedAttivita.urlLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          bg="#ffcc00"
                          color="black"
                          leftIcon={<FaExternalLinkAlt />}
                          borderRadius="12px"
                          fontWeight="600"
                          _hover={{
                            bg: '#e6b800',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
                          }}
                          transition="all 0.3s ease"
                          size="lg"
                          fontFamily="Montserrat, sans-serif"
                        >
                          Visita il progetto
                        </Button>
                      )}

                      {selectedAttivita.ytLink && (
                        <Button
                          as="a"
                          href={selectedAttivita.ytLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          bg="#ff0000"
                          color="white"
                          leftIcon={<FaYoutube />}
                          borderRadius="12px"
                          fontWeight="600"
                          _hover={{
                            bg: '#cc0000',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(255, 0, 0, 0.3)',
                          }}
                          transition="all 0.3s ease"
                          size="lg"
                          fontFamily="Montserrat, sans-serif"
                        >
                          Guarda il video
                        </Button>
                      )}
                    </VStack>
                  </Box>
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
};

export default ArchivioAttivita;
